import { AdsAssistantOrgSettingsActivityCoachingCard } from "@/components/ads-assistant/AdsAssistantOrgSettingsActivityCoachingCard";
import { AdsAssistantOrgSettingsGhlSyncCard } from "@/components/ads-assistant/AdsAssistantOrgSettingsGhlSyncCard";
import { AdsAssistantOrgSettingsArchiveList } from "@/components/ads-assistant/AdsAssistantOrgSettingsArchiveList";
import { useAdsAssistantOrgSettings } from "@/hooks/useAdsAssistantOrgSettings";
import { ADS_FEED_RETENTION_MAX_DAYS } from "@/lib/adsAssistantOrgSettings";

export function AdsAssistantOrgSettingsSection(props: { scopedOrgId: string | null }) {
  const { scopedOrgId } = props;
  const model = useAdsAssistantOrgSettings(scopedOrgId);

  if (!scopedOrgId) {
    return (
      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
        Select an organization workspace to edit Ads org settings.
      </p>
    );
  }

  const { settingsLoaded, loading, msg, platformRows, setPlatformRows, prefs, setPrefs, save } = model;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-zinc-900 space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Org Settings</h3>
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400 max-w-prose">
          Platforms shown in Ads ingestion, feed retention, suggestion defaults, and archive hints. Changes merge with your full
          Location Tools checklist config (manual rows and nudges are preserved).
        </p>
      </div>

      {msg ? (
        <p
          className={
            "rounded-lg px-3 py-2 text-sm " +
            (msg.ok ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100" : "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200")
          }
        >
          {msg.text}
        </p>
      ) : null}

      <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-zinc-900/30 px-4 py-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Platforms &amp; slots per day</span>
          <button
            type="button"
            className="text-xs text-blue-600 hover:underline dark:text-blue-400"
            disabled={loading}
            onClick={() =>
              setPlatformRows((rows) => [
                ...rows,
                { rowId: crypto.randomUUID(), key: "new_platform", label: "New platform", daily_slots: 1 },
              ])
            }
          >
            + Add platform
          </button>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Keys must be lowercase with optional underscores. Automated pulls exist for <strong>facebook</strong> and{" "}
          <strong>google</strong>; other keys are tracked for logging and the roadmap.
        </p>
        <div className="space-y-3">
          {platformRows.map((p, idx) => (
            <div
              key={p.rowId ?? `platform-row-${idx}`}
              className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-zinc-950/50 px-3 py-3"
            >
              <label className="text-[11px] text-gray-600 dark:text-gray-400 min-w-[5rem] flex-1">
                Key
                <input
                  className="mt-0.5 w-full text-xs font-mono border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-zinc-900"
                  value={p.key}
                  onChange={(e) =>
                    setPlatformRows((rows) => rows.map((r, i) => (i === idx ? { ...r, key: e.target.value } : r)))
                  }
                  disabled={loading}
                />
              </label>
              <label className="text-[11px] text-gray-600 dark:text-gray-400 flex-[2] min-w-[10rem]">
                Display label
                <input
                  className="mt-0.5 w-full text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-zinc-900"
                  value={p.label}
                  onChange={(e) =>
                    setPlatformRows((rows) => rows.map((r, i) => (i === idx ? { ...r, label: e.target.value } : r)))
                  }
                  disabled={loading}
                />
              </label>
              <label className="text-[11px] text-gray-600 dark:text-gray-400 w-24">
                Slots
                <input
                  type="number"
                  min={1}
                  max={20}
                  className="mt-0.5 w-full text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-zinc-900"
                  value={p.daily_slots}
                  onChange={(e) =>
                    setPlatformRows((rows) =>
                      rows.map((r, i) =>
                        i === idx ? { ...r, daily_slots: Math.min(20, Math.max(1, parseInt(e.target.value, 10) || 1)) } : r,
                      ),
                    )
                  }
                  disabled={loading}
                />
              </label>
              <button
                type="button"
                className="text-xs text-red-600 hover:underline mb-0.5"
                disabled={loading}
                onClick={() => setPlatformRows((rows) => rows.filter((_, i) => i !== idx))}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs text-gray-700 dark:text-gray-300 block">
          Hot feed retention (days)
          <input
            type="number"
            min={1}
            max={ADS_FEED_RETENTION_MAX_DAYS}
            className="mt-1 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900"
            value={prefs.feed_retention_days}
            onChange={(e) =>
              setPrefs((pr) => ({
                ...pr,
                feed_retention_days: Math.min(
                  ADS_FEED_RETENTION_MAX_DAYS,
                  Math.max(1, parseInt(e.target.value, 10) || 1),
                ),
              }))
            }
            disabled={loading}
          />
          <span className="text-[10px] text-gray-500 dark:text-gray-400">Maximum {ADS_FEED_RETENTION_MAX_DAYS} days (platform cap).</span>
        </label>

        <label className="text-xs text-gray-700 dark:text-gray-300 block">
          Min exemplars before template sign-off
          <input
            type="number"
            min={3}
            max={50}
            className="mt-1 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900"
            value={prefs.min_exemplars_for_template_signoff}
            onChange={(e) =>
              setPrefs((pr) => ({
                ...pr,
                min_exemplars_for_template_signoff: Math.min(50, Math.max(3, parseInt(e.target.value, 10) || 5)),
              }))
            }
            disabled={loading}
          />
        </label>

        <label className="inline-flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100 sm:col-span-2">
          <input
            type="checkbox"
            checked={prefs.show_recent_logger_names}
            onChange={(e) => setPrefs((pr) => ({ ...pr, show_recent_logger_names: e.target.checked }))}
            disabled={loading}
          />
          Show recent logger names on Ads activity strip (when enabled in UI later)
        </label>

        <label className="text-xs text-gray-700 dark:text-gray-300 block sm:col-span-2">
          Suggestion stats default scope
          <select
            className="mt-1 w-full max-w-md text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900"
            value={prefs.suggestion_stats_scope_default}
            onChange={(e) =>
              setPrefs((pr) => ({
                ...pr,
                suggestion_stats_scope_default: e.target.value === "location" ? "location" : "org",
              }))
            }
            disabled={loading}
          >
            <option value="org">Organization-wide</option>
            <option value="location">My location (competitive)</option>
          </select>
        </label>

        <div className="sm:col-span-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-3 py-3 space-y-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
            <input
              type="checkbox"
              checked={prefs.archive_export.enabled}
              onChange={(e) =>
                setPrefs((pr) => ({
                  ...pr,
                  archive_export: { ...pr.archive_export, enabled: e.target.checked },
                }))
              }
              disabled={loading}
            />
            Archive before purge (Supabase Storage)
          </label>
          <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-snug">
            When enabled, the nightly retention job uploads one <strong>.jsonl</strong> file per org (expired posts) to the project
            bucket set in server env <code className="rounded bg-gray-100 px-1 dark:bg-zinc-800">ADS_ARCHIVE_BUCKET</code>, then deletes
            those rows. If the bucket is missing or upload fails, rows are <strong>not</strong> deleted (check cron logs / warnings).
            Google Drive and arbitrary S3 targets are not wired in v1.
          </p>
          <input
            className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900"
            placeholder="Optional path label (e.g. acme_ads) — becomes ads-archive/{label}/… for your team"
            value={prefs.archive_export.destination_hint ?? ""}
            onChange={(e) =>
              setPrefs((pr) => ({
                ...pr,
                archive_export: {
                  ...pr.archive_export,
                  destination_hint: e.target.value.slice(0, 2000),
                },
              }))
            }
            disabled={loading || !prefs.archive_export.enabled}
          />
          <AdsAssistantOrgSettingsArchiveList enabled={prefs.archive_export.enabled} ready={settingsLoaded} />
        </div>
      </div>

      <AdsAssistantOrgSettingsActivityCoachingCard
        prefs={prefs}
        setPrefs={setPrefs}
        platformCount={platformRows.length}
        loading={loading}
      />

      <AdsAssistantOrgSettingsGhlSyncCard prefs={prefs} setPrefs={setPrefs} loading={loading} />

      <button
        type="button"
        disabled={loading || !settingsLoaded || platformRows.length === 0}
        onClick={() => void save()}
        className="rounded-lg bg-blue-600 text-white px-4 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-700 dark:hover:bg-blue-600"
      >
        {loading ? "Saving…" : "Save org Ads settings"}
      </button>
    </div>
  );
}
